import React from "react";
import { Badge, Button, Card } from "../../components/ui";
import { change, hours, int, money, moneyShort, pct, tierLabel, vsLabel } from "../format";
import { BarChart, BarList, Donut, Progress, Ring, Sparkline, StackBar } from "../components/charts";
import { Avatar, DashCard, Delta, InfoTip, ListRow, Rank, SectionLabel, Stat, StatGrid } from "../components/parts";
import { cardState } from "../components/cardState";
import TargetButton from "../components/TargetButton";

// Store tab. Every section reads its OWN part of /api/dashboard/store
// (?part=kpis|trend|floor|pipeline|team|clients), so one slow part shows a
// skeleton in its card and never blanks the page.

const TIER_RANGES = { top_vic: "£150k+", vic: "£70–150k", potential: "£30–70k", core: "£10–30k", entry: "under £10k", prospect: "£0" };

/**
 * A top KPI tile. Every tile has the same four rows — label, value (+ spark),
 * delta, foot — and the foot sits on the bottom edge, so a row of tiles is the
 * same height with their foot lines aligned whatever each one carries.
 */
function Tile({ k, label, value, delta, foot, spark, tone }) {
  return (
    <Card padding="none" className="dash-card dash-kpi">
      <div className="dash-kpi__label">
        {label}
        <InfoTip k={k} />
      </div>
      <div className="dash-kpi__row">
        <span className="dash-kpi__val dash-num">{value}</span>
        {spark && spark.length > 1 ? <Sparkline values={spark} tone={tone} /> : null}
      </div>
      <div className="dash-kpi__delta">{delta}</div>
      <div className="dash-kpi__foot">{foot}</div>
    </Card>
  );
}

function Hero({ q, trendQ }) {
  const d = q.data || {};
  const k = d.kpis || {};
  const trend = (trendQ?.data?.trend || []).map((t) => Number(t.turnover_pence) || 0);
  const state = cardState(q, { what: "the store KPIs", shape: "card" });
  if (state) {
    return (
      <div className="dash-grid dash-grid--4 dash-grid--eq">
        <Card padding="none" className="dash-card dash-kpi dash-kpi--state">{state}</Card>
      </div>
    );
  }
  const growth = k.growth_pct ?? change(k.turnover_pence, k.turnover_prev_pence);
  return (
    <div className="dash-grid dash-grid--4 dash-grid--eq">
      <Tile
        k="turnover"
        label="Turnover"
        value={moneyShort(k.turnover_pence)}
        delta={
          <>
            <Delta value={growth} label={vsLabel(d.period?.prev_label)} />
            {growth != null ? <InfoTip k="growth" /> : <span className="dash-kpi__muted">No previous period</span>}
          </>
        }
        foot={`${int(k.deals_won)} deals won · ${d.period?.label || ""}`}
        spark={trend.slice(-8)}
        tone={1}
      />
      <Tile k="dealSize" label="Avg deal size" value={moneyShort(k.avg_deal_pence)} delta={<span className="dash-kpi__muted">per won deal</span>} foot="Turnover ÷ deals won" tone={2} />
      <Tile
        k="hoursClose"
        label="Avg time to close"
        value={hours(k.avg_hours_to_close)}
        delta={<span className="dash-kpi__muted">{k.avg_hours_to_close != null ? `≈ ${(k.avg_hours_to_close / 24).toFixed(1)} days` : "No deals closed yet"}</span>}
        foot="Request created → won"
        tone={3}
      />
      <Tile
        k="deposits"
        label="Open deposits"
        value={moneyShort(k.open_deposits_pence)}
        delta={<Badge size="sm" tone="muted">{int(k.open_deposits_count)} deals</Badge>}
        foot={`Held right now · ${int(k.high_ticket_count)} won over £30k`}
        tone={5}
      />
    </div>
  );
}

function Floor({ q, waAvailable }) {
  const f = q.data?.floor_today || {};
  const state = cardState(q, { what: "today on the floor", lines: 2 });
  if (state) return <Card padding="none" className="dash-card">{state}</Card>;
  const automated = Number(f.automated_intake_today) || 0;
  const cells = [
    <Stat key="new" label="New requests" info="floor" value={int(f.new_inbound_requests)} foot={automated ? `+${int(automated)} AI / WhatsApp intake, not counted` : "people, today"} />,
    <Stat key="won" label="Won today" value={int(f.won_today)} foot={f.new_inbound_requests ? `${int(f.new_inbound_requests)} new : ${int(f.won_today)} won` : "deals closed"} />,
    <Stat key="links" label="Payment links" value={int(f.payment_links_sent)} foot="sent today" />,
    <Stat key="closer" label="Top closer" value={f.top_closer?.name ? String(f.top_closer.name).split(" ")[0] : "—"} foot={f.top_closer ? `${money(f.top_closer.amount_pence)} today` : "No wins yet today"} />,
  ];
  if (waAvailable && f.wa_new_inbound != null) cells.push(<Stat key="wa" label="New WhatsApp chats" value={int(f.wa_new_inbound)} foot="first message today" />);
  return <StatGrid className="dash-stats--row">{cells}</StatGrid>;
}

function Trend({ q, storeId, canSetTarget }) {
  const d = q.data || {};
  const rows = d.trend || [];
  const state = cardState(q, { what: "the trend", shape: "card", empty: !rows.length, emptyText: "No turnover recorded yet." });
  return (
    <DashCard
      title="Turnover trend"
      info="trend"
      meta={d.period?.label ? `${d.period.label} highlighted` : null}
      tools={canSetTarget && storeId ? <TargetButton kind="store" id={storeId} label="Store target" /> : null}
    >
      {state || <BarChart values={rows.map((t) => Number(t.turnover_pence) || 0)} labels={rows.map((t) => t.label)} format={moneyShort} label="Turnover trend" height={230} />}
    </DashCard>
  );
}

function Pipeline({ q }) {
  const p = q.data?.pipeline || {};
  const map = (rows, key = "stage") => (rows || []).map((s) => ({ name: s.label || s[key], value: s.count }));
  const sum = (rows) => (rows || []).reduce((s, r) => s + (Number(r.count) || 0), 0);
  const state = cardState(q, { what: "the pipeline" });
  const automated = Number(p.automated_intake) || 0;
  return (
    <DashCard title="Pipeline" info="pipeline" meta="live">
      {state || (
        <div className="dash-stack dash-stack--loose">
          <div>
            <div className="dash-kpi-label">Opportunities · <b className="dash-num">{int(sum(p.opportunities))}</b></div>
            <StackBar rows={map(p.opportunities)} />
          </div>
          <div>
            <div className="dash-kpi-label">Leads · <b className="dash-num">{int(sum(p.leads))}</b></div>
            <StackBar rows={map(p.leads)} />
          </div>
          <div>
            <div className="dash-kpi-label">Opportunity source</div>
            <StackBar rows={map(p.sources, "source")} />
            {automated ? <p className="dash-muted dash-gap-top">People only — {int(automated)} AI / WhatsApp intake requests this period are left out.</p> : null}
          </div>
        </div>
      )}
    </DashCard>
  );
}

function Team({ q, isManager, onViewRep }) {
  const team = [...(q.data?.team || [])].sort((a, b) => (Number(b.turnover_pence) || 0) - (Number(a.turnover_pence) || 0));
  const top = Math.max(1, ...team.map((r) => Number(r.turnover_pence) || 0));
  const state = cardState(q, { what: "the team", empty: !team.length, emptyText: "No reps on this store yet." });
  const rate = (v) => (v == null ? "—" : pct(v));
  return (
    <DashCard title="Team" info="team" meta={q.data?.period?.label}>
      {state || (
        <div className="dash-table-wrap">
          <table className="dash-table">
            <thead>
              <tr>
                <th scope="col">Rep</th>
                <th scope="col">Turnover</th>
                <th scope="col">Won</th>
                <th scope="col">Lost</th>
                <th scope="col">Win rate</th>
                <th scope="col">Lost rate</th>
                <th scope="col">Lead → opp</th>
                <th scope="col">&gt;£30k</th>
                <th scope="col">Quotes</th>
                <th scope="col">Emails</th>
                {isManager ? <th scope="col"><span className="dash-sr">Open</span></th> : null}
              </tr>
            </thead>
            <tbody>
              {team.map((r, i) => (
                <tr key={r.user_id}>
                  <td>
                    <span className="dash-who">
                      <span className="dash-rank-n dash-num">{i + 1}</span>
                      <Avatar initials={r.initials} name={r.name} i={i} />
                      {r.name}
                    </span>
                  </td>
                  <td className="dash-num">
                    {moneyShort(r.turnover_pence)}
                    <span className="dash-mbar" aria-hidden="true"><Progress value={((Number(r.turnover_pence) || 0) / top) * 100} /></span>
                  </td>
                  <td className="dash-num">{int(r.won)}</td>
                  <td className="dash-num">{int(r.lost)}</td>
                  <td className={`dash-num${r.win_rate == null ? "" : r.win_rate >= 40 ? " dash-good" : r.win_rate < 30 ? " dash-bad" : ""}`}>{rate(r.win_rate)}</td>
                  <td className="dash-num">{rate(r.lost_rate)}</td>
                  <td className="dash-num">{rate(r.lead_conversion_pct)}</td>
                  <td className="dash-num">{r.high_ticket ? int(r.high_ticket) : "–"}</td>
                  <td className="dash-num">{int(r.quotes_sent)}</td>
                  <td className="dash-num">{int(r.emails_sent)}</td>
                  {isManager ? (
                    <td>
                      <Button variant="ghost" size="sm" data-btn="dashboard-team-view-day" onClick={() => onViewRep(r.user_id)}>
                        My Day
                      </Button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashCard>
  );
}

function Clients({ q, teamQ }) {
  const c = q.data?.clients || {};
  const state = (what) => cardState(q, { what });
  const segments = (c.segments || []).map((s) => ({ name: `${s.label || tierLabel({ tier: s.tier })}${TIER_RANGES[s.tier] ? ` · ${TIER_RANGES[s.tier]}` : ""}`, value: s.count }));
  const countries = (c.countries || []).slice(0, 5).map((s) => ({ name: s.name || s.code, value: s.count }));
  const rest = (c.countries || []).slice(5).reduce((s, x) => s + (Number(x.count) || 0), 0);
  if (rest) countries.push({ name: "Other", value: rest });
  const natTotal = (c.nationalities || []).reduce((s, x) => s + (Number(x.count) || 0), 0);
  const nationalities = (c.nationalities || []).map((n) => ({ name: n.name, value: n.count }));
  const ret = c.retention || {};
  const prof = c.profile_completion || {};
  const missing = [...(prof.fields || [])].sort((a, b) => (a.filled_pct ?? 0) - (b.filled_pct ?? 0)).slice(0, 3);
  const cel = c.celebrations_next7 || {};
  const top = c.top_clients || [];
  const engaged = teamQ?.data?.engaged_users || [];

  return (
    <>
      <SectionLabel>Clients</SectionLabel>
      <div className="dash-grid dash-grid--3 dash-grid--eq">
        <DashCard title="Contact segmentation" info="segmentation" meta={c.total != null ? `${int(c.total)} clients` : null}>
          {state("segments") || (segments.length ? <BarList rows={segments} format={int} /> : <p className="dash-empty">No clients yet.</p>)}
        </DashCard>
        <DashCard title="Where clients live" info="country" meta="by address">
          {state("countries") ||
            (countries.length ? (
              <>
                <Donut rows={countries} center={c.local_pct != null ? pct(c.local_pct) : "—"} sub="local" />
                {c.local_pct != null ? (
                  <div className="dash-mix">
                    <div className="dash-kpi-label">Customer mix</div>
                    <StackBar tall rows={[{ name: "Local", value: c.local_pct }, { name: "International", value: Math.max(0, 100 - c.local_pct) }]} />
                  </div>
                ) : null}
              </>
            ) : (
              <p className="dash-empty">No client addresses recorded yet.</p>
            ))}
        </DashCard>
        <DashCard title="Top nationalities" info="nationality" meta="top 5">
          {state("nationalities") ||
            (nationalities.length ? (
              <BarList rows={nationalities} tone={2} format={(v) => (c.total ? pct((v / c.total) * 100) : int(v))} />
            ) : (
              <p className="dash-empty">No nationalities recorded yet.</p>
            ))}
          {!q.isLoading && c.total && natTotal < c.total ? (
            <div className="dash-callout">Nationality is missing on <b>{pct(((c.total - natTotal) / c.total) * 100)}</b> of clients{nationalities.length >= 5 ? " (or outside the top 5)" : ""}.</div>
          ) : null}
        </DashCard>
      </div>
      <div className="dash-grid dash-grid--3 dash-grid--eq">
        <DashCard title="Top clients" info="topClients" meta={q.data?.period?.label}>
          {state("top clients") ||
            (top.length ? (
              <div className="dash-list">
                {top.slice(0, 5).map((t, i) => (
                  <ListRow key={t.user_id} lead={<Rank n={i + 1} />} title={t.name} sub={`${tierLabel(t)} · ${int(t.purchases)} purchase${Number(t.purchases) === 1 ? "" : "s"}`} right={moneyShort(t.turnover_pence)} />
                ))}
              </div>
            ) : (
              <p className="dash-empty">No purchases in this period.</p>
            ))}
        </DashCard>
        <DashCard title="Client health">
          {state("client health") || (
            <div className="dash-stack dash-stack--loose">
              <div className="dash-health">
                <Ring value={ret.rate_pct ?? 0} label={ret.rate_pct != null ? pct(ret.rate_pct) : "—"} sub="retained" size={84} />
                <div className="dash-health__text">
                  <span className="dash-health__title">Retention rate <InfoTip k="retention" /></span>
                  <span className="dash-num">{int(ret.start)} at start · {int(ret.new)} new · {int(ret.end)} at end</span>
                </div>
              </div>
              <div>
                <div className="dash-kpi-label dash-kpi-label--split">
                  <span>Profile completion <InfoTip k="profile" /></span>
                  <b className="dash-num">{pct(prof.pct)}</b>
                </div>
                <Progress value={prof.pct ?? 0} label="Profile completion" />
                {missing.length ? (
                  <div className="dash-muted dash-gap-top">
                    Most missing: {missing.map((f, i) => <span key={f.field}>{i ? " · " : ""}<b>{f.label}</b> {pct(100 - (f.filled_pct ?? 0))}</span>)}
                  </div>
                ) : null}
              </div>
              <div>
                <div className="dash-kpi-label">Coming up · next 7 days</div>
                <StatGrid className="dash-stats--three">
                  <Stat label="Birthdays" value={int(cel.birthdays)} />
                  <Stat label="1-month" value={int(cel.followup_1m)} />
                  <Stat label="1-year" value={int(cel.anniversary_1y)} />
                </StatGrid>
              </div>
            </div>
          )}
        </DashCard>
        <DashCard title="Most engaged users" info="engaged" meta="last 7 days">
          {cardState(teamQ, { what: "engaged users" }) ||
            (engaged.length ? (
              <div className="dash-list">
                {engaged.slice(0, 5).map((u, i) => (
                  <ListRow key={u.user_id} lead={<Avatar name={u.name} i={i} />} title={u.name} sub={i === 0 ? "Most active" : "activities logged"} right={int(u.activity_count)} />
                ))}
              </div>
            ) : (
              <p className="dash-empty">No activity logged in the last 7 days.</p>
            ))}
        </DashCard>
      </div>
    </>
  );
}

/** `parts` = { kpis, trend, floor, pipeline, team, clients } — one query each. */
export default function StoreTab({ parts, me, isManager, canSetTarget, onViewRep }) {
  return (
    <div className="dash-stack dash-stack--page">
      <Hero q={parts.kpis} trendQ={parts.trend} />
      <SectionLabel>Today on the floor</SectionLabel>
      <Floor q={parts.floor} waAvailable={!!me?.wa_available} />
      <div className="dash-grid dash-grid--main">
        <Trend q={parts.trend} storeId={me?.store?.id} canSetTarget={canSetTarget} />
        <Pipeline q={parts.pipeline} />
      </div>
      <Team q={parts.team} isManager={isManager} onViewRep={onViewRep} />
      <Clients q={parts.clients} teamQ={parts.team} />
    </div>
  );
}
